import { useStarredEntities } from '@backstage/plugin-catalog-react';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core/styles';
import { DocsHero } from './DocsHero';
import { DocListPanel } from './DocListPanel';
import { DocsTreePreviewCard } from './DocsTreePreviewCard';
import { RecommendedDocsSection } from './RecommendedDocsSection';
import { useDocCatalog } from './useDocCatalog';
import { useRecommendedDocs } from './useRecommendedDocs';
import { StarIcon } from '../../components/icons';
import { RecentlyViewedPanel } from '../home/RecentlyViewedPanel';
import { useRecentVisits } from '../home/useRecentVisits';

const MAX_VISITS = 5;

/*
 * The TechDocs landing page, replacing the stock owned/all entity table.
 *
 * Company and Team recommended are curated by admins in
 * /admin/recommended-docs (capped server-side at 6 and 3-per-team); Starred
 * docs is the reader's own pinned set; Recently viewed reuses the home page's
 * own panel and visit history (`home/RecentlyViewedPanel`, `useRecentVisits`)
 * rather than a docs-specific reimplementation. There is no doc popularity or
 * freshness data in the portal today, so the design's "Most read" and
 * "Recently updated" panels are deliberately absent rather than filled with
 * invented numbers.
 *
 * Documentation tree replaces the hero's old "Just show me the tree!"
 * button, which read as a form submit sitting right next to the search
 * field. A card that previews the top-level tree with its own "Browse the
 * full tree" link at the bottom reads as a destination, not a submit.
 */
const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
    padding: '32px 40px 40px 40px',
    // Phone: the mobile top app bar carries the page, and 402px has no room for
    // the desktop gutter.
    [theme.breakpoints.down('xs')]: {
      gap: 20,
      padding: '16px 16px 24px 16px',
    },
  },
  panels: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gridTemplateRows: 'auto auto auto',
    alignItems: 'start',
    gap: 20,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'auto',
      gap: 12,
    },
  },
  recommended: {
    gridColumn: 1,
    gridRow: '1 / span 3',
    [theme.breakpoints.down('xs')]: {
      gridColumn: 'auto',
      gridRow: 'auto',
    },
  },
  starred: {
    gridColumn: 2,
    gridRow: 1,
    [theme.breakpoints.down('xs')]: {
      gridColumn: 'auto',
      gridRow: 'auto',
    },
  },
  treePreview: {
    gridColumn: 2,
    gridRow: 2,
    [theme.breakpoints.down('xs')]: {
      gridColumn: 'auto',
      gridRow: 'auto',
    },
  },
  recentlyViewed: {
    gridColumn: 2,
    gridRow: 3,
    [theme.breakpoints.down('xs')]: {
      gridColumn: 'auto',
      gridRow: 'auto',
    },
  },
  empty: {
    padding: 20,
    borderRadius: 12,
    background: 'var(--bui-bg-neutral-2)',
    fontSize: 13,
    color: 'var(--bui-fg-secondary)',
  },
}));

export const DocsLandingPage = () => {
  const classes = useStyles();
  const { value, loading: catalogLoading, error: catalogError } = useDocCatalog();
  const {
    company,
    team,
    loading: recommendedLoading,
    error: recommendedError,
  } = useRecommendedDocs();
  const { starredEntities } = useStarredEntities();
  const { loading: visitsLoading, visits } = useRecentVisits(MAX_VISITS);

  if (catalogLoading || recommendedLoading || visitsLoading) return <Progress />;
  if (catalogError) return <ResponseErrorPanel error={catalogError} />;
  if (recommendedError) return <ResponseErrorPanel error={recommendedError} />;

  const starred = (value?.all ?? []).filter(doc =>
    starredEntities.has(doc.entityRef),
  );

  return (
    <div className={classes.root}>
      <DocsHero />

      {/* No section heading: the mock runs hero → panels, and the panels
          name themselves. */}
      <div className={classes.panels}>
        <div className={classes.recommended}>
          <RecommendedDocsSection
            company={company}
            team={team}
            companyEmptyLabel="Your company does not have recommended docs yet."
            teamEmptyLabel="Your teams don't have any recommended docs yet."
          />
        </div>
        <div className={classes.starred}>
          <DocListPanel
            title="Starred docs"
            icon={StarIcon}
            docs={starred}
            emptyLabel="Star an entity in the catalog to pin its docs here."
          />
        </div>
        <div className={classes.treePreview}>
          <DocsTreePreviewCard />
        </div>
        <div className={classes.recentlyViewed}>
          <RecentlyViewedPanel visits={visits} action={null} />
        </div>
      </div>
    </div>
  );
};
